import { useMemo } from 'react'
import type { DiagramScene, SceneNode } from '../api/types'
import type { ParsedStatement } from '../utils/types'
import { titleCase } from '../utils/titleCase'
import styles from './Workspace.module.css'

interface NodeWorkspaceProps {
    scene: DiagramScene
    nodeId: string
    statement: ParsedStatement | null
}

export function NodeWorkspace({ scene, nodeId, statement }: NodeWorkspaceProps) {
    const node: SceneNode | null = scene.nodes.find(n => n.id === nodeId) ?? null
    if (!node) return null

    // Connection counts
    const { inCount, outCount } = useMemo(() => {
        let inC = 0, outC = 0
        for (const e of scene.edges) {
            if (e.targetId === nodeId) inC++
            if (e.sourceId === nodeId) outC++
        }
        return { inCount: inC, outCount: outC }
    }, [scene.edges, nodeId])

    // Role label
    const role = node.semanticRole.toUpperCase()
    const roleLabel = role.includes('ACTION') ? 'Action'
        : role.includes('SOURCE') ? 'Source'
        : role.includes('RESULT') ? 'Result'
        : role.includes('HUB') ? 'Hub'
        : role.includes('INTERMEDIATE') ? 'Entity'
        : role.includes('ACTOR') ? 'Subject'
        : titleCase(node.semanticRole)

    return (
        <div className={styles.workspace}>
            <div className={styles.wsHeader}>
                <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                        <div className={styles.nodeMeta}>
                            {node.actorLabel && <>by <strong>{titleCase(node.actorLabel)}</strong></>}
                            {node.locationLabel && <> · at <strong>{titleCase(node.locationLabel)}</strong></>}
                        </div>
                        <div className={styles.nodeStructure}>
                            <span className={styles.connCount}>&#9668; {inCount} in · &#9658; {outCount} out</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={styles.roleBadge}>{roleLabel}</span>
                    </div>
                </div>

                {/* Needs / Yields */}
                {statement && (
                    <div className={`${styles.nodeMeta} mt-2 p-2 bg-white/5 rounded border border-white/5`}>
                        {statement.needs.length > 0 && <div><span className="text-accent opacity-70">needs</span> {statement.needs.join(', ')}</div>}
                        {statement.yields.length > 0 && <div><span className="text-success opacity-70">yields</span> {statement.yields.join(', ')}</div>}
                    </div>
                )}
            </div>

            <div className={styles.wsDivider} />

            {/* Body intentionally empty — insight surfacing removed */}
            <div className={styles.wsBody}>
                <div aria-hidden style={{ height: 80, flexShrink: 0 }} />
            </div>
        </div>
    )
}
